import { proxy } from 'valtio';

export type OperationStatus = 'idle' | 'pending' | 'running' | 'error';

export type OperationKey =
  | 'bulk-categorize'
  | 'bulk-generate-rules'
  | 'hybrid-categorize'
  | `sync:${string}`
  | `rule:${string}`
  | `rule-group:${string}`;
export interface OperationState {
  status: OperationStatus;
  error: string | null;
  label: string | null;
  startedAt: number | null;
}

const defaultState: OperationState = {
  status: 'idle',
  error: null,
  label: null,
  startedAt: null,
};

/** Reactive store – subscribe from any component via `useSnapshot(operationsStore)` */
export const operationsStore = proxy<Record<string, OperationState>>({});

/** Internal map of live operations (AbortController + promise) */
const activeOps = new Map<
  string,
  { controller: AbortController; promise: Promise<void> }
>();

function setOperationState(key: string, patch: Partial<OperationState>) {
  if (!operationsStore[key]) {
    operationsStore[key] = { ...defaultState };
  }
  Object.assign(operationsStore[key], patch);
}

function resetOperation(key: string) {
  if (operationsStore[key]) {
    Object.assign(operationsStore[key], defaultState);
  }
}

/** Externally set the state of an operation (e.g. from SSE event handlers). */
export function setExternalOperationState(
  key: OperationKey,
  patch: Partial<OperationState>
): void {
  setOperationState(key, patch);
}

/** Externally reset an operation to idle. */
export function resetExternalOperation(key: OperationKey): void {
  resetOperation(key);
}

/**
 * Start a long-running operation.
 * The `executeFn` receives an AbortSignal
 * it should forward to fetch/SSE calls.
 * If an operation with the same key is already running, this is a no-op.
 */
export function startOperation(
  key: OperationKey,
  executeFn: (signal: AbortSignal) => Promise<void>,
  label?: string
): void {
  if (activeOps.has(key)) return; // prevent double-start

  const controller = new AbortController();

  setOperationState(key, {
    status: 'running',
    error: null,
    label: label ?? key,
    startedAt: Date.now(),
  });

  const promise = executeFn(controller.signal)
    .catch((err) => {
      if (controller.signal.aborted) return; // expected abort – not an error
      const message = err instanceof Error ? err.message : String(err);
      setOperationState(key, { status: 'error', error: message });
    })
    .finally(() => {
      activeOps.delete(key);
      // Only reset to idle if still running (error handler may have already set 'error')
      if (operationsStore[key]?.status === 'running') {
        resetOperation(key);
      }
    });

  activeOps.set(key, { controller, promise });
}

/** Abort a running operation by key. */
export function stopOperation(key: string): void {
  const op = activeOps.get(key);
  if (!op) return;

  op.controller.abort();
  activeOps.delete(key);
  resetOperation(key);
}

/** Check whether an operation is currently running. */
export function isOperationRunning(key: string): boolean {
  return activeOps.has(key);
}

/** Get the AbortSignal for a running operation (useful for linking). */
export function getOperationSignal(key: string): AbortSignal | undefined {
  return activeOps.get(key)?.controller.signal;
}

/**
 * Track an already-running promise in the operations store.
 * Unlike `startOperation`, this does NOT create an AbortController or
 * guard against double-starts. Use for simple fire-and-forget calls
 * (e.g. a single POST) where you just want banner visibility.
 */
export function trackOperation<T>(
  key: OperationKey,
  promise: Promise<T>,
  label?: string
): Promise<T> {
  setOperationState(key, {
    status: 'running',
    error: null,
    label: label ?? key,
    startedAt: Date.now(),
  });

  return promise
    .then((result) => {
      resetOperation(key);
      return result;
    })
    .catch((err) => {
      const message = err instanceof Error ? err.message : String(err);
      setOperationState(key, { status: 'error', error: message });
      throw err; // re-throw so callers still see the error
    });
}
